import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import SkeletonLoader from "../common/SkeletonLoader";
import { ConsultantRow, ConsultantServiceFeeRow, ConsultantServiceRow, ProcedureSessionRow, SessionFilter } from "./types";
import { getMasterIdByProcedureType, getProcedureByMasterId, getProcedureSteps, ProcedureEntry } from "../../registry/procedureRegistry";
import { isCompletedStatus, normalize } from "./utils";

interface TreatmentPanelProps {
    visitIdValue: number | null;
    patientIdValue: string;
    catalog: ProcedureEntry[];
    catalogLoading: boolean;
    consultants: ConsultantRow[];
    consultantServices: ConsultantServiceRow[];
    consultantServiceFees: ConsultantServiceFeeRow[];
    sessions: ProcedureSessionRow[];
    setSessions: React.Dispatch<React.SetStateAction<ProcedureSessionRow[]>>;
    showToast: (_args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
    isTouchViewport: boolean;
}

export default function TreatmentPanel({
    visitIdValue,
    patientIdValue,
    catalog,
    catalogLoading,
    consultants,
    consultantServices,
    consultantServiceFees,
    sessions,
    setSessions,
    showToast,
    isTouchViewport,
}: TreatmentPanelProps) {
    const [procedureSearch, setProcedureSearch] = useState("");
    const [selectedMasterId, setSelectedMasterId] = useState("");
    const [procedureTeeth, setProcedureTeeth] = useState("");
    const [selectedConsultantId, setSelectedConsultantId] = useState("");
    const [selectedConsultantServiceId, setSelectedConsultantServiceId] = useState("");
    const [savingProcedure, setSavingProcedure] = useState(false);
    const [sessionFilter, setSessionFilter] = useState<SessionFilter>("inprogress");
    const [sessionBusyId, setSessionBusyId] = useState<number | null>(null);

    const selectedProcedure = useMemo(() => {
        if (!selectedMasterId) return null;
        return getProcedureByMasterId(selectedMasterId);
    }, [selectedMasterId]);

    const filteredProcedures = useMemo(() => {
        const q = normalize(procedureSearch);
        if (!q) return catalog;
        return catalog.filter((p) => {
            const name = normalize(String(p.display_name || p.name || ""));
            const code = normalize(String(p.master_id || ""));
            return name.includes(q) || code.includes(q);
        });
    }, [catalog, procedureSearch]);

    const selectedProcedureDbId = useMemo(() => {
        const rawId = selectedProcedure?.id;
        const parsed = Number(rawId);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }, [selectedProcedure]);

    const consultantServiceOptions = useMemo(() => {
        const cid = Number(selectedConsultantId);
        if (!cid) return [];

        const feeRows = consultantServiceFees.filter((f) => Number(f.consultant_id) === cid);
        const allowed = new Set<number>();
        for (const fee of feeRows) {
            if (selectedProcedureDbId && Number(fee.procedure_master_id || 0) !== selectedProcedureDbId) continue;
            const sid = Number(fee.service_id);
            if (sid) allowed.add(sid);
        }
        return consultantServices.filter((s) => allowed.has(Number(s.id)));
    }, [selectedConsultantId, selectedProcedureDbId, consultantServiceFees, consultantServices]);

    const selectedConsultantServiceFee = useMemo(() => {
        const cid = Number(selectedConsultantId);
        const sid = Number(selectedConsultantServiceId);
        if (!cid || !sid) return 0;
        const row = consultantServiceFees.find((f) => Number(f.consultant_id) === cid && Number(f.service_id) === sid);
        return Number(row?.fee || 0);
    }, [selectedConsultantId, selectedConsultantServiceId, consultantServiceFees]);

    useEffect(() => {
        if (!selectedConsultantId) {
            if (selectedConsultantServiceId) setSelectedConsultantServiceId("");
            return;
        }
        const hasSelected = consultantServiceOptions.some((s) => String(s.id) === String(selectedConsultantServiceId));
        if (!hasSelected) {
            setSelectedConsultantServiceId(consultantServiceOptions[0] ? String(consultantServiceOptions[0].id) : "");
        }
    }, [consultantServiceOptions, selectedConsultantId, selectedConsultantServiceId]);

    const selectedProcedureRequiresTooth = !!selectedProcedure?.requires_tooth;
    const normalizeSteps = (masterId: string | null | undefined) => {
        const steps = getProcedureSteps(masterId);
        return (steps || []).map((st, idx) => ({
            stepNo: idx + 1,
            key: `step_${idx + 1}`,
            label: typeof st === "string" ? st : (st.label || `Step ${idx + 1}`),
            chart_mutation: typeof st === "string" ? "NONE" : (st.chart_mutation || "NONE"),
        }));
    };
    const selectedProcedureSteps = useMemo(() => {
        if (!selectedMasterId) return [];
        return normalizeSteps(selectedMasterId);
    }, [selectedMasterId]);

    const loadVisitSessions = async () => {
        if (!visitIdValue) return;
        try {
            const res = await apiGet(`/api/doctor/procedures/by-visit/${visitIdValue}`);
            if (Array.isArray(res)) setSessions(res.slice() as ProcedureSessionRow[]);
        } catch { }
    };

    const handleStartProcedure = async () => {
        if (!selectedMasterId) { showToast({ message: "Select a procedure first.", type: "warning" }); return; }
        if (selectedProcedureRequiresTooth && !procedureTeeth.trim()) { showToast({ message: "Tooth mapping is required for this procedure.", type: "warning" }); return; }
        if (selectedConsultantId && !selectedConsultantServiceId) {
            showToast({ message: "Select consultant service for this procedure.", type: "warning" });
            return;
        }

        setSavingProcedure(true);
        try {
            const steps = selectedProcedureSteps;
            const stepCount = steps.length || 1;
            const rawTeeth = procedureTeeth.trim();
            const toothParts = rawTeeth ? rawTeeth.split(/[,\\s]+/).filter(Boolean) : [];
            const toothNo = toothParts.length > 0 ? toothParts[0] : null;
            const targetRef = toothParts.length > 1 ? toothParts.join(",") : (rawTeeth || null);
            const consultantName = selectedConsultantId && consultants.length ? consultants.find((c) => String(c.id) === String(selectedConsultantId))?.name || null : null;

            await apiPost("/api/doctor/procedures", {
                visit_id: visitIdValue,
                patient_id: patientIdValue,
                procedure_type: selectedProcedure?.display_name || selectedProcedure?.name || selectedProcedure?.master_id,
                procedure_master_id: selectedMasterId,
                tooth_no: toothNo,
                target_ref: targetRef,
                current_step: 0,
                step_no: 1,
                total_steps: stepCount,
                consultant: consultantName,
                consultant_service_id: selectedConsultantServiceId || null,
                consultant_fee: selectedConsultantServiceId ? selectedConsultantServiceFee : null,
            });
            showToast({ message: "Procedure started.", type: "success" });
            setProcedureSearch(""); setSelectedMasterId(""); setProcedureTeeth(""); setSelectedConsultantId(""); setSelectedConsultantServiceId("");
            await loadVisitSessions();
        } catch (err: any) {
            showToast({ message: err?.message || "Failed to start procedure.", type: "error" });
        } finally {
            setSavingProcedure(false);
        }
    };

    const advanceStep = async (session: ProcedureSessionRow) => {
        const sessionId = Number(session.id);
        setSessionBusyId(sessionId);
        try {
            const masterId = getMasterIdByProcedureType(session.procedure_type || "");
            const steps = normalizeSteps(masterId);
            if (steps.length === 0) { showToast({ message: "No predefined steps for this procedure.", type: "warning" }); return; }
            const currentStep = Number(session.step_no || 0);
            const totalSteps = Number(session.total_steps || steps.length || 1);
            const nextStepNo = Math.min(currentStep + 1, totalSteps);
            const stepDef = steps[nextStepNo - 1] || steps[0];
            const isLast = nextStepNo >= totalSteps;

            await apiPost(`/api/doctor/procedures/${sessionId}/step`, {
                step_no: nextStepNo,
                step_key: stepDef.key || `step_${nextStepNo}`,
                step_label: stepDef.label || `Step ${nextStepNo}`,
                chart_mutation: stepDef.chart_mutation || "NONE",
                status: isLast ? "COMPLETED" : "IN_PROGRESS",
            });
            await loadVisitSessions();
        } catch (err: any) {
            showToast({ message: err?.message || "Failed to advance step.", type: "error" });
        } finally {
            setSessionBusyId(null);
        }
    };

    if (!visitIdValue) {
        return (
            <div className="mod-standalone-window">
                <div className="mod-empty-state" style={{ height: '300px', border: 'none' }}>
                    <div className="mod-empty-icon-wrapper" style={{ background: '#d1fae5', color: '#10b981' }}>
                        <span className="mod-empty-icon" style={{ opacity: 1 }}>🦷</span>
                    </div>
                    <h2>No Active Visit</h2>
                    <p>Select or start a visit to prescribe and track treatments.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="mod-window-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <div>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            🦷 Active Treatments
                        </h2>
                        <p className="mod-text-muted">Plan and execute clinical procedures.</p>
                    </div>
                </div>
            </div>

            {/* Treatment Entry Area */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ position: 'relative' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)', display: 'block', marginBottom: '6px' }}>Procedure Type</label>
                        <input
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '0.95rem' }}
                            value={procedureSearch}
                            onChange={(e) => { setProcedureSearch(e.target.value); if (!e.target.value.trim()) setSelectedMasterId(""); }}
                            placeholder="Search procedure catalog..."
                        />
                        {filteredProcedures.length > 0 && procedureSearch && !selectedMasterId && (
                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, width: '100%', background: '#fff', border: '1px solid #cbd5e1', zIndex: 10, borderRadius: '8px', boxShadow: 'var(--mod-shadow-md)', maxHeight: '200px', overflowY: 'auto' }}>
                                {filteredProcedures.map((proc) => (
                                    <div key={proc.master_id || proc.name} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontWeight: 600, fontSize: '0.9rem' }} onClick={() => { setSelectedMasterId(proc.master_id || ""); setProcedureSearch((proc.display_name || proc.name || proc.master_id) || ""); }}>
                                        {proc.display_name || proc.name || proc.master_id}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)', display: 'block', marginBottom: '6px' }}>Tooth No.</label>
                        <input
                            style={{ width: '100px', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '0.95rem', textAlign: 'center' }}
                            value={procedureTeeth}
                            onChange={(e) => setProcedureTeeth(e.target.value)}
                            placeholder="e.g. 16"
                        />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
                    <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)', display: 'block', marginBottom: '6px' }}>Consultant (optional)</label>
                        <select
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '0.95rem', background: '#fff' }}
                            value={selectedConsultantId}
                            onChange={(e) => {
                                setSelectedConsultantId(e.target.value);
                                setSelectedConsultantServiceId("");
                            }}
                        >
                            <option value="">No consultant</option>
                            {consultants.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)', display: 'block', marginBottom: '6px' }}>Consultant Service</label>
                        <select
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '0.95rem', background: '#fff' }}
                            value={selectedConsultantServiceId}
                            onChange={(e) => setSelectedConsultantServiceId(e.target.value)}
                            disabled={!selectedConsultantId}
                        >
                            <option value="">{selectedConsultantId ? "Select service" : "Select consultant first"}</option>
                            {consultantServiceOptions.map((s) => (
                                <option key={s.id} value={String(s.id)}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ minWidth: '140px', padding: '10px 12px', borderRadius: '10px', background: '#ecfdf5', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#047857', textTransform: 'uppercase' }}>Consultant Fee</div>
                        <div style={{ fontSize: '1rem', fontWeight: 800, color: '#065f46' }}>₹{Number(selectedConsultantServiceFee || 0).toLocaleString("en-IN")}</div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                        style={{ padding: '10px 24px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--mod-mint), #059669)', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: (!selectedMasterId || savingProcedure) ? 'not-allowed' : 'pointer', opacity: (!selectedMasterId || savingProcedure) ? 0.6 : 1, boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)' }}
                        onClick={handleStartProcedure} 
                        disabled={savingProcedure || !selectedMasterId}
                    >
                        {savingProcedure ? "Adding..." : "+ Add Treatment"}
                    </button>
                </div>
            </div>

            {/* Treatment List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '0.9rem', color: 'var(--mod-text-muted)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Session Plan ({sessions.length})</h3>
                </div>

                {sessions.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: '0.9rem' }}>
                        No treatments scheduled for this session.
                    </div>
                ) : (
                    sessions.map((session) => {
                        const isDone = isCompletedStatus(session.status);
                        const busy = sessionBusyId === Number(session.id);
                        return (
                            <div key={session.id} style={{ display: 'flex', flexDirection: 'column', background: '#ffffff', border: isDone ? '1px solid #bbf7d0' : '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: 'var(--mod-shadow-sm)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                        <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: isDone ? '#d1fae5' : '#e0f2fe', color: isDone ? '#059669' : '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem' }}>
                                            {session.tooth_no || 'GEN'}
                                        </div>
                                        <div>
                                            <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: 'var(--mod-text-main)' }}>{session.procedure_type || "Unknown Procedure"}</h4>
                                            <div style={{ display: 'flex', gap: '8px', fontSize: '0.8rem', color: 'var(--mod-text-muted)' }}>
                                                <span>Cost: <strong style={{ color: 'var(--mod-text-main)' }}>₹{session.consultant_fee ? session.consultant_fee.toFixed(2) : '1,995.00'}</strong></span>
                                                {session.consultant && <span>• By {session.consultant}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, background: isDone ? '#d1fae5' : '#fef3c7', color: isDone ? '#059669' : '#d97706', border: `1px solid ${isDone ? '#a7f3d0' : '#fde68a'}` }}>
                                        {isDone ? 'Completed' : 'Planned'}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginTop: '4px' }}>
                                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9' }}></div>
                                        Billing Status: <span style={{ fontWeight: 700, color: '#0ea5e9' }}>Pending Invoice</span>
                                    </div>
                                    
                                    {!isDone && (
                                        <button 
                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderRadius: '20px', background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', fontSize: '0.85rem', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1 }}
                                            onClick={() => advanceStep(session)}
                                            disabled={busy}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                                            Mark Step Complete
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
